import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Clock, CheckCircle2, XCircle, AlarmClock, RefreshCw, Play, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const API = window.location.origin + "/api";

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Rome",
    });
  } catch {
    return iso;
  }
}

function formatDateOnly(s) {
  if (!s) return "—";
  // s in formato YYYY-MM-DD
  const parts = String(s).split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return s;
}

// Tabella dettagli appuntamenti cancellati per un singolo run.
// Caricata in lazy quando l'utente espande la riga.
function RunDetails({ executedAt }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [details, setDetails] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(`${API}/appointments/auto-purge-cancelled/run-details`, {
        params: { executed_at: executedAt },
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!cancelled) setDetails(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || "Errore nel caricamento dettagli");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [executedAt]);

  if (loading) {
    return (
      <div className="px-3 py-3 flex items-center gap-2 text-xs text-slate-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Caricamento dettagli…
      </div>
    );
  }
  if (error) {
    return <div className="px-3 py-3 text-xs text-red-600">{error}</div>;
  }
  const events = details?.cancelled_events || [];
  if (events.length === 0) {
    return (
      <div className="px-3 py-3 text-xs text-slate-400 italic">
        Nessun dettaglio disponibile per questo run.
        {(details?.total ?? 0) > 0 && (
          <span className="block mt-1 text-[10px]">
            (Il run è precedente all'introduzione dello snapshot dettagliato.)
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="mt-2 border-t border-slate-200 pt-2" data-testid="auto-purge-run-details">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 px-1">
        Appuntamenti cancellati ({events.length})
      </div>
      <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-slate-600">
              <th className="px-2 py-1.5 font-semibold">Esito</th>
              <th className="px-2 py-1.5 font-semibold">Data/ora</th>
              <th className="px-2 py-1.5 font-semibold">Paziente</th>
              <th className="px-2 py-1.5 font-semibold">Titolo</th>
              <th className="px-2 py-1.5 font-semibold">Medico</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev, i) => (
              <tr
                key={ev.id || i}
                className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                data-testid={`auto-purge-event-${i}`}
              >
                <td className="px-2 py-1.5">
                  {ev.deleted_ok ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" title="Eliminato" />
                  ) : (
                    <XCircle className="w-3 h-3 text-red-600" title="Errore eliminazione" />
                  )}
                </td>
                <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">
                  {formatDateTime(ev.start_time)}
                </td>
                <td className="px-2 py-1.5 text-slate-800 font-medium">
                  {ev.contact_name || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-2 py-1.5 text-slate-500 max-w-[200px] truncate" title={ev.title}>
                  {ev.title || "—"}
                </td>
                <td className="px-2 py-1.5 text-slate-500">
                  {ev.doctor_name || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AutoPurgeStatusCard() {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [expandedAt, setExpandedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(
        `${API}/appointments/auto-purge-cancelled/history?limit=5`,
        { withCredentials: true }
      );
      setData(resp);
    } catch (err) {
      if (err.response?.status === 403) {
        setHidden(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const runNow = useCallback(async () => {
    if (running) return;
    setRunning(true);
    try {
      const { data: resp } = await axios.post(
        `${API}/appointments/auto-purge-cancelled/run`,
        {},
        { withCredentials: true, timeout: 600000 }
      );
      if (resp?.skipped) {
        toast.info("Run già in corso, riprova tra qualche istante");
      } else {
        const del = resp?.deleted ?? 0;
        const fail = resp?.failed ?? 0;
        const tot = resp?.total ?? 0;
        if (tot === 0) {
          toast.success("Nessun appuntamento cancellato da rimuovere");
        } else {
          toast.success(`${del}/${tot} eliminati${fail ? ` · ${fail} falliti` : ""}`);
        }
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'esecuzione");
    } finally {
      setRunning(false);
    }
  }, [running, load]);

  useEffect(() => {
    load();
  }, [load]);

  if (hidden) return null;

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-3"
      data-testid="auto-purge-status-card"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <AlarmClock className="w-4 h-4 text-emerald-600" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
            Cancellazione automatica notturna
          </h3>
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full">
            22:00
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading || running}
            className="rounded-lg h-8 text-xs"
            data-testid="auto-purge-refresh-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Ricarica
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={runNow}
            disabled={running || loading}
            className="rounded-lg h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="auto-purge-run-now-btn"
            title="Esegui subito la scansione (stesso comportamento del cron delle 22:00)"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
            Esegui ora
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        Ogni sera alle <strong>22:00</strong> (Europe/Rome) viene eseguita una scansione automatica
        del <strong>&laquo;Calendario Generale&raquo;</strong> <strong>dal giorno corrente in poi</strong>
        (prossimi 365 giorni): tutti gli appuntamenti con stato <strong>&laquo;Cancellati&raquo;</strong>
        vengono eliminati definitivamente da GHL.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Prossimo run
          </div>
          <div className="text-sm font-semibold text-slate-800 mt-1" data-testid="auto-purge-next-run">
            {loading ? "—" : formatDateTime(data?.next_run_iso)}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Schedulazione
          </div>
          <div className="text-sm font-semibold text-slate-800 mt-1">
            {data?.schedule_label || "Ogni sera alle 22:00"}
          </div>
        </div>
      </div>

      <div className="pt-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
          Ultimi 5 run automatici
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : !data?.runs || data.runs.length === 0 ? (
          <div className="text-xs text-slate-400 italic px-3 py-3 bg-slate-50 rounded-lg border border-dashed border-slate-200">
            Nessun run automatico registrato finora.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="auto-purge-history-list">
            {data.runs.map((r, idx) => {
              const ok = !r.failed || r.failed === 0;
              const isExpanded = expandedAt === r.executed_at;
              const canExpand = (r.total ?? 0) > 0;
              return (
                <div
                  key={r.executed_at || idx}
                  className="bg-slate-50 border border-slate-200 rounded-lg text-xs"
                  data-testid={`auto-purge-run-${idx}`}
                >
                  <div className="flex items-center justify-between gap-3 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => canExpand && setExpandedAt(isExpanded ? null : r.executed_at)}
                      disabled={!canExpand}
                      className={`flex items-center gap-2 min-w-0 flex-1 text-left ${
                        canExpand ? "hover:opacity-80 cursor-pointer" : "cursor-default"
                      }`}
                      data-testid={`auto-purge-run-toggle-${idx}`}
                      aria-expanded={isExpanded}
                    >
                      {canExpand ? (
                        isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        )
                      ) : (
                        <span className="w-3.5 h-3.5 shrink-0" />
                      )}
                      {ok ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                      )}
                      <span className="font-semibold text-slate-700">
                        {formatDateTime(r.executed_at)}
                      </span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">
                        range {formatDateOnly(r.start_date)}
                        {r.end_date && r.end_date !== r.start_date ? ` → ${formatDateOnly(r.end_date)}` : ""}
                      </span>
                    </button>
                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                      <span className="font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        {r.deleted ?? 0} eliminati
                      </span>
                      {(r.failed ?? 0) > 0 && (
                        <span className="font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                          {r.failed} falliti
                        </span>
                      )}
                      {(r.total ?? 0) === 0 && (
                        <span className="text-slate-400 italic">nessun cancellato</span>
                      )}
                    </div>
                  </div>
                  {isExpanded && canExpand && (
                    <div className="px-3 pb-3">
                      <RunDetails executedAt={r.executed_at} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
